import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useNavigate } from 'react-router-dom';

export function Settings() {
  const { user, profile, tier, session, signOut, notifyPremiumFromCoupon } = useAuth();
  const navigate = useNavigate();

  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponSuccess, setCouponSuccess] = useState('');
  const [couponError, setCouponError] = useState('');

  const email = profile?.email ?? user?.email ?? '';
  const isPremium = tier === 'premium';

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleRedeemCoupon = async (e) => {
    e.preventDefault();
    setCouponSuccess('');
    setCouponError('');
    const token = session?.access_token;
    if (!token || !user?.id) {
      setCouponError('Inicia sesión para canjear un código.');
      return;
    }
    const trimmed = couponCode.trim();
    if (!trimmed) {
      setCouponError('Ingresa un código.');
      return;
    }

    setCouponLoading(true);
    try {
      const res = await fetch('/.netlify/functions/redeem-coupon', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          code: trimmed.toUpperCase(),
          userId: user.id,
        }),
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.ok && data.success) {
        notifyPremiumFromCoupon();
        setCouponSuccess(
          '¡Bienvenido a Premium! Recarga la página para ver todos los beneficios.'
        );
        setCouponCode('');
      } else {
        setCouponError(data.error || 'No se pudo canjear el código.');
      }
    } catch {
      setCouponError('Error de red. Intenta de nuevo.');
    } finally {
      setCouponLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <h1 className="text-3xl font-bold text-brand-dark">Configuración</h1>

      <Card className="p-6">
        <h2 className="text-xl font-bold border-b pb-4 mb-4 flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiUser} />
          Perfil de Usuario
        </h2>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center text-2xl text-gray-500 font-bold">
            {email?.[0]?.toUpperCase() || 'U'}
          </div>
          <div>
            <p className="font-medium text-lg">{email}</p>
            <p className="text-sm text-gray-500 capitalize">Plan: {tier}</p>
          </div>
        </div>
        <Button variant="outline" onClick={handleLogout} className="text-gray-600 border-gray-300">
          Cerrar Sesión
        </Button>
      </Card>

      {(!isPremium || couponSuccess) && (
        <Card className="p-6">
          <h2 className="text-xl font-bold flex items-center gap-2 mb-2">
            <SafeIcon icon={FiIcons.FiGift} />
            ¿Tienes un código Beta?
          </h2>
          {couponSuccess ? (
            <p className="text-sm font-medium text-green-600">{couponSuccess}</p>
          ) : (
            <>
              <p className="text-gray-600 text-sm mb-4">
                Ingresa tu código para acceder a todas las funciones Premium sin costo durante el
                período Beta.
              </p>
              <form onSubmit={handleRedeemCoupon} className="space-y-4">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(ev) => {
                    setCouponCode(ev.target.value);
                    setCouponError('');
                    setCouponSuccess('');
                  }}
                  placeholder="Ej. LATINOSBETA"
                  autoComplete="off"
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-transparent"
                  disabled={couponLoading}
                />
                {couponError ? (
                  <p className="text-sm font-medium text-red-600">{couponError}</p>
                ) : null}
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full py-3 text-base"
                  disabled={couponLoading}
                >
                  {couponLoading ? 'Canjeando…' : 'Canjear Código'}
                </Button>
              </form>
            </>
          )}
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiCreditCard} />
            Suscripción y Facturación
          </h2>
        </div>

        <div className="p-6 bg-gray-50">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-bold">Plan Actual:</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${isPremium ? 'bg-brand-red text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  {isPremium ? 'Premium' : 'Gratis'}
                </span>
              </div>
              <p className="text-gray-600 text-sm max-w-md">
                {isPremium
                  ? 'Tienes acceso total a todas las herramientas de valoración, listados ilimitados y análisis avanzado.'
                  : 'Estás en el plan gratuito. Actualiza para desbloquear valoraciones exactas e ilimitadas.'}
              </p>
            </div>

            {!isPremium ? (
              <Button className="w-full md:w-auto px-8 py-3 text-lg shadow-md">Actualizar a Premium</Button>
            ) : (
              <Button variant="outline" className="w-full md:w-auto">
                Gestionar Facturación
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
